import { useCallback, useMemo, useRef } from 'react'
import { getStereotypes, getSystemUsers, getUsersForCombo } from 'src/endpoints'
import styled from 'styled-components'

import { Col, FieldSet, Flex, Legend, Row } from '@/components/Layout'
import {
	CheckBoxFormField,
	Form,
	NamingInputFormField,
	SelectFormField,
	TextAreaFormField,
	TextFormField,
	useGetNamingConfig,
} from '@/components/UberForm'
import { FormlessFormField } from '@/components/UberForm/FormField'
import NumberFormField from '@/components/UberForm/Helpers/NumberFormField'
import { AnyObject } from '@/components/UberForm/types'
import { useApi } from '@/endpoints/hooks'
import { StereotypeDto, StructureObjectDto } from '@/endpoints/models'
import { WriteMappingData, WriteMappingDataEnums } from '@/endpoints/schemas'
import { PageType } from '@/enums'
import {
	useAppContext,
	useAppDispatch,
	useCustomAttributesInitValues,
} from '@/hooks'
import { OpenedWriteMappingData } from '@/store/modules/writeMapping/types'
import { MainContentWrap } from '@/styles'
import { booleanString } from '@/utils/booleans'
import { uniqueNameValidator } from '@/utils/validators'

import { CustomAttributes } from '../../../../components/CustomAttributes/CustomAttributes'
import { useOpenTab } from '../../../../components/Dependencies/hooks/useOpenTab'

interface Props {
	data: OpenedWriteMappingData
	editMode: boolean
	node?: StructureObjectDto
	onChange: (v: Partial<WriteMappingData>) => void
	/** Selected nodes for batch edit */
	selectedNodes?: StructureObjectDto[]
	systemNodeId: number
}

export const Overview = ({
	onChange,
	data,
	editMode,
	systemNodeId,
	node,
	selectedNodes,
}: Props) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()

	const refForm = useRef<Form<WriteMappingData>>(null)

	const stereotypes = useApi(
		getStereotypes({ type: StereotypeDto.TypeEnum.MAPPING_WRITE }),
	)

	const { openTabById } = useOpenTab()

	const [users, isUserLoading] = useApi(getUsersForCombo())

	const [systemUsers] = useApi(getSystemUsers(systemNodeId))

	const {
		initialValuesCustomAttributes,
		parseCustomAttribute,
		customAttributes,
		getCustomAttributesReset,
	} = useCustomAttributesInitValues(
		data.form,
		StructureObjectDto.TypeEnum.MAPPING_WRITE,
	)

	const writeMappingStates = useMemo(
		() => [
			{
				label: t('MAPPING_STATUS_LOGICAL_IN_PROGRESS'),
				value: WriteMappingDataEnums.WriteMappingStatusEnum.LOGICAL_IN_PROGRESS,
			},
			{
				label: t('MAPPING_STATUS_LOGICAL_COMPLETED'),
				value: WriteMappingDataEnums.WriteMappingStatusEnum.LOGICAL_COMPLETED,
			},
			{
				label: t('MAPPING_STATUS_PHYSICAL_IN_PROGRESS'),
				value:
					WriteMappingDataEnums.WriteMappingStatusEnum.PHYSICAL_IN_PROGRESS,
			},
			{
				label: t('MAPPING_STATUS_PHYSICAL_COMPLETED'),
				value: WriteMappingDataEnums.WriteMappingStatusEnum.PHYSICAL_COMPLETED,
			},
			{
				label: t('MAPPING_STATUS_FROZEN'),
				value: WriteMappingDataEnums.WriteMappingStatusEnum.FROZEN,
			},
		],
		[t],
	)

	const handleChange = useCallback(
		(
			item: Partial<WriteMappingData>,
			source?: FormlessFormField<WriteMappingData>,
		) => {
			if (source) {
				const { name } = source.props

				if (name === 'responsiblePersonId' || name === 'responsibleAnalystId') {
					const user = users?.find((u) => u.id === item[name])

					const userName =
						name === 'responsiblePersonId'
							? 'responsiblePersonName'
							: 'responsibleAnalystName'

					onChange({
						[name]: user?.id,
						[userName]: user?.value,
					})

					return
				}

				if (name === 'ownerId') {
					const owner = systemUsers?.find((u) => u.id === item[name])

					onChange({
						ownerId: owner?.id,
						ownerName: owner?.name,
					})

					return
				}

				if (name === 'orderPriority') {
					onChange({
						orderPriority: item[name],
					})

					return
				}

				if (name === 'stereotypeId') {
					const stereotype = stereotypes.data?.find((u) => u.id === item[name])

					const { customAttributesForm, customAttributesRedux } =
						getCustomAttributesReset(stereotype?.id)

					refForm.current?.setValues(customAttributesForm)

					onChange({
						[name]: stereotype?.id,
						customAttributes: customAttributesRedux,
					})

					return
				}

				onChange(
					parseCustomAttribute(
						{
							[name]: item[name],
						},
						data.form.customAttributes,
						customAttributes,
					),
				)
			}
		},
		[
			data.form.customAttributes,
			getCustomAttributesReset,
			onChange,
			parseCustomAttribute,
			stereotypes,
			systemUsers,
			users,
		],
	)

	const namingConfig = useGetNamingConfig(data.form as AnyObject, node)

	return (
		<MainContentWrap>
			<Form<WriteMappingData>
				onChange={handleChange}
				defaultValues={{
					...data.form,
					...initialValuesCustomAttributes,
				}}
				disabled={!editMode}
				key={booleanString(editMode)}
				enableFieldHighlight
			>
				<Row $alignItems="flex-end">
					<Col size="medium">
						<TextFormField
							title={t('MAPPING_NAME')}
							name="name"
							initialValue={data.form.name}
							validators={[uniqueNameValidator(dispatch, data.original.name)]}
							disabled={Boolean(selectedNodes)}
						/>
					</Col>
					<Col size="medium">
						<NamingInputFormField
							node={node}
							name="code"
							title={t('MAPPING_CODE')}
							callNamingWorkingDataParams={namingConfig}
							initialValue={data.form.code}
							disabled={Boolean(selectedNodes)}
						/>
					</Col>
					<Col
						size="medium"
						onClick={() => openTabById(data.form.targetTableId as number)}
					>
						<TextFormField
							title={t('TARGET_TABLE')}
							name="targetTableFullPath"
							initialValue={data.form.targetTableFullPath}
							titleContainer={data.form.targetTableFullPath}
							disabled
							tooltip={t('CLICK_TO_OPEN_TABLE')}
						/>
					</Col>
					<Col size="medium">
						<SelectFormField
							name="stereotypeId"
							title={t('MAPPING_STEREOTYPE')}
							initialValue={data.form.stereotypeId}
							fieldIsLoading={stereotypes.reloading}
							options={stereotypes.data || []}
							valueKey="id"
							labelKey="name"
							isNumeric
							allowEmpty
							isDisabled={!editMode}
						/>
					</Col>
					<Col size="small">
						<Flex>
							<CheckBoxFormField
								name="distinct"
								title={t('MAPPING_DISTINCT')}
								initialValue={data.form.distinct}
							/>
						</Flex>
					</Col>
				</Row>
				<Row>
					<Col size="big">
						<TextAreaFormField
							name="description"
							title={t('MAPPING_DESCRIPTION')}
							initialValue={data.form.description}
						/>
					</Col>
					<Col size="big">
						<TextAreaFormField
							name="comment"
							title={t('MAPPING_COMMENT')}
							initialValue={data.form.comment}
						/>
					</Col>
				</Row>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_PROJECT_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SelectFormField
								name="status"
								title={t('MAPPING_STATUS')}
								initialValue={data.form.status}
								options={writeMappingStates}
								valueKey="value"
								labelKey="label"
								isDisabled={!editMode}
							/>
						</Col>
						<Col size="medium">
							<SelectFormField
								name="responsiblePersonId"
								title={t('RESPONSIBLE_PERSON')}
								options={users || []}
								fieldIsLoading={isUserLoading}
								valueKey="id"
								labelKey="value"
								initialValue={data.form.responsiblePersonId}
								isNumeric
								allowEmpty
								isDisabled={!editMode}
							/>
						</Col>
						<Col size="medium">
							<SelectFormField
								name="responsibleAnalystId"
								title={t('RESPONSIBLE_ANALYST')}
								options={users || []}
								fieldIsLoading={isUserLoading}
								valueKey="id"
								labelKey="value"
								initialValue={data.form.responsibleAnalystId}
								isNumeric
								allowEmpty
								isDisabled={!editMode}
							/>
						</Col>
					</Row>
				</FieldSetGap>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_TECHNICAL_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SelectFormField
								name="ownerId"
								title={t('NODE_OWNER')}
								options={systemUsers || []}
								fieldIsLoading={isUserLoading}
								initialValue={data.form.ownerId}
								valueKey="id"
								labelKey="name"
								isNumeric
								allowEmpty
								clearable
								isDisabled={!editMode}
							/>
						</Col>
						<Col size="big">
							<TextAreaFormField
								name="condition"
								title={t('MAPPING_CONDITION')}
								initialValue={data.form.condition}
							/>
						</Col>
						<Col size="medium">
							<NumberFormField
								name="orderPriority"
								title={t('ORDER_OF_EXECUTION')}
								initialValue={data.form.orderPriority}
								decimals={2}
							/>
						</Col>
					</Row>
				</FieldSetGap>
				<CustomAttributes
					$pageType={PageType.OVERVIEW_PAGE}
					customAttributes={customAttributes}
				/>
			</Form>
		</MainContentWrap>
	)
}

const FieldSetGap = styled(FieldSet)`
	margin-top: 20px;
`
